// src/data/crisisSupport.js
// Crisis support for The Art of Story. The course's safety notes (in the
// modules, the challenges, the Quick Guide and two tools) all point readers
// in the seven markets to the same help, through CRISIS_HELP below, and the
// Resources page lists every country's lines (components/resources/SupportLines).
//
// ⚠️ The numbers are not kept here. They live in the fleet's one table,
// packages/crisis-support/src/services.js, with the date each was verified
// and the page it was verified on. Change a number THERE, never here. This
// app's readers are adults, so it uses the 'adult' list.
//
// Imported by relative path, not a Vite alias, so the file also loads in
// plain Node (tests, and the Knowledge Hub importer). Data files that import
// it must name it with its .js extension for the same reason: an import Node
// can't resolve makes the importer stub it out, and the sentence would reach
// Marlow without its numbers.

import {
  crisisSupportTable,
  serviceById,
  detectCountry,
  emergencyCallAdvice,
  COUNTRY_KEYS,
  DEFAULT_COUNTRY,
  DIRECTORIES
} from '../../../../packages/crisis-support/src/index.js';

export const CRISIS_SUPPORT = crisisSupportTable('adult');

export { detectCountry, emergencyCallAdvice, DEFAULT_COUNTRY, DIRECTORIES };

// Each market's 24/7 line, by id. The UK and Ireland share Samaritans, and
// Canada and the US share 988, so the sentence names each once.
const CRISIS_LINES = {
  australia: 'au-lifeline',
  newzealand: 'nz-1737',
  india: 'in-tele-manas',
  uk: 'uk-samaritans',
  ireland: 'ie-samaritans',
  canada: 'ca-988',
  usa: 'us-988'
};

// How the sentence names each country ("the UK", not "United Kingdom").
const SHORT_NAMES = { uk: 'the UK', usa: 'the US' };
const countryName = (key) => SHORT_NAMES[key] || CRISIS_SUPPORT[key].name;

// What to dial. "Triple Zero (000)" becomes "000" and "Call or text 1737"
// becomes "1737": in a run of seven numbers, the digits are what matter.
const dialled = (number) => number.match(/\(([^)]+)\)\s*$/)?.[1] ?? number.replace(/^call or text /i, '');

// A line whose name is its number (1737, 988) is shown as the number alone.
const lineFor = (key) => {
  const { name, number } = serviceById(CRISIS_LINES[key]);
  const dial = dialled(number);
  return name.replace(/\D/g, '').includes(dial.replace(/\D/g, '')) ? dial : `${name} ${dial}`;
};

const listOf = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`);

// "999 or 112 in the UK and Ireland, 911 in Canada and the US": countries in
// the table's order, neighbours with the same number named together.
const byCountry = (valueFor) => {
  const groups = [];
  for (const key of COUNTRY_KEYS) {
    const value = valueFor(key);
    const last = groups.at(-1);
    if (last && last.value === value) last.keys.push(key);
    else groups.push({ value, keys: [key] });
  }
  return groups.map(({ value, keys }) => `${value} in ${listOf(keys.map(countryName))}`).join(', ');
};

/**
 * "call your local emergency number (…) or a crisis line (…)", each bracket
 * naming all seven markets, for the course's safety notes. Lower case, no full
 * stop: each note leads into it and ends it in its own words.
 */
export const CRISIS_HELP =
  `call your local emergency number (${byCountry((key) => dialled(CRISIS_SUPPORT[key].emergency.number))})` +
  ` or a crisis line (${byCountry(lineFor)})`;
